import { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { setItems } from '../store/slices/itemsSlice';
import { api } from '../services/api';
import DashboardChart, { ChartData, ChartDataPoint, TimeInterval } from '../components/DashboardChart';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBoxOpen,
  faArrowUp, faArrowDown, faMinus,
  faChartLine, faChartBar, faChartPie, faHashtag,
  faPlus, faEdit, faSave, faSliders, faGripLines, faTimes,
  faTrash, faPalette, faCalendarAlt, faSync,
  faLayerGroup, faCubes, faGem, faWallet,
} from '@fortawesome/free-solid-svg-icons';
import { useI18n } from '../hooks/useI18n';
import '../styles/Dashboard.css';

/* ──────────────────────────── Types ──────────────────────────── */

interface ItemRecord {
  id: string;
  name: string;
  description?: string;
  condition?: string;
  location?: string;
  purchasePrice?: number;
  purchaseCurrency?: string;
  purchaseDate?: string;
  isFavorite?: boolean;
  createdAt: string;
  updatedAt: string;
  category?: { id: string; name: string };
  tags?: string[];
}

interface KPI {
  id: string;
  label: string;
  value: number;
  formatted: string;
  change: number;
  changeLabel: string;
  icon: typeof faBoxOpen;
  color: string;
}

interface DashboardChartConfig {
  id: string;
  title: string;
  titleKey?: string;
  type: 'bar' | 'pie' | 'line' | 'number';
  data: { labels?: string[]; values?: number[]; colors?: string[]; value?: number | string; label?: string; change?: number };
  description?: string;
  descKey?: string;
  visible?: boolean;
  interval?: TimeInterval;
  currentPeriod?: Date;
  size?: 'normal' | 'wide';
}

// Map chart IDs to i18n keys for dynamic translation
const CHART_I18N: Record<string, { titleKey: string; descKey: string }> = {
  'value-trend':        { titleKey: 'dashboard.valueTrend',          descKey: 'dashboard.valueTrendDesc' },
  'activity-trend':     { titleKey: 'dashboard.itemsAdded',          descKey: 'dashboard.itemsAddedDesc' },
  'condition-breakdown':{ titleKey: 'dashboard.conditionBreakdown',  descKey: 'dashboard.conditionDesc' },
  'category-breakdown': { titleKey: 'dashboard.categoryDistribution',descKey: 'dashboard.categoryDesc' },
  'price-distribution': { titleKey: 'dashboard.priceDistribution',   descKey: 'dashboard.priceDistDesc' },
  'top-items':          { titleKey: 'dashboard.mostValuable',        descKey: 'dashboard.mostValuableDesc' },
  'location-breakdown': { titleKey: 'dashboard.byLocation',          descKey: 'dashboard.byLocationDesc' },
  'cumulative-value':   { titleKey: 'dashboard.cumulativeValue',     descKey: 'dashboard.cumulativeDesc' },
  'favorites-trend':    { titleKey: 'dashboard.favoritesOverTime',   descKey: 'dashboard.favTrendDesc' },
  'tags-distribution':  { titleKey: 'dashboard.topTags',             descKey: 'dashboard.topTagsDesc' },
  'purchase-place':     { titleKey: 'dashboard.purchaseSources',     descKey: 'dashboard.purchaseDesc' },
  'avg-price-category': { titleKey: 'dashboard.avgByCategory',       descKey: 'dashboard.avgCatDesc' },
};

/* ──────────────────────────── Helpers ──────────────────────────── */

type DateSel = (i: ItemRecord) => Date | null;
type ValSel = (i: ItemRecord) => number;

const INTERVAL_WINDOW: Record<TimeInterval, number> = { day: 14, week: 8, month: 6, quarter: 4, year: 5 };

const startOfInterval = (d: Date, iv: TimeInterval): Date => {
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  switch (iv) {
    case 'day': return r;
    case 'week': { r.setDate(r.getDate() - r.getDay()); return r; }
    case 'month': return new Date(r.getFullYear(), r.getMonth(), 1);
    case 'quarter': return new Date(r.getFullYear(), Math.floor(r.getMonth() / 3) * 3, 1);
    case 'year': return new Date(r.getFullYear(), 0, 1);
  }
};

const addInterval = (d: Date, iv: TimeInterval, n: number): Date => {
  const r = new Date(d);
  switch (iv) {
    case 'day': r.setDate(r.getDate() + n); return r;
    case 'week': r.setDate(r.getDate() + n * 7); return r;
    case 'month': r.setMonth(r.getMonth() + n); return r;
    case 'quarter': r.setMonth(r.getMonth() + n * 3); return r;
    case 'year': r.setFullYear(r.getFullYear() + n); return r;
  }
};

const fmtInterval = (d: Date, iv: TimeInterval): string => {
  switch (iv) {
    case 'day': return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    case 'week': return `W${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    case 'month': return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    case 'quarter': return `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`;
    case 'year': return `${d.getFullYear()}`;
  }
};

const buildTimeSeries = (items: ItemRecord[], iv: TimeInterval, period: Date, dateSel: DateSel, valSel: ValSel) => {
  const pts = INTERVAL_WINDOW[iv];
  const pStart = startOfInterval(period, iv);
  const buckets = Array.from({ length: pts }, (_, i) => {
    const s = addInterval(pStart, iv, i - (pts - 1));
    return { start: s, end: addInterval(s, iv, 1), label: fmtInterval(s, iv), value: 0 };
  });
  items.forEach(item => {
    const raw = dateSel(item);
    if (!raw) return;
    const d = new Date(raw);
    for (const b of buckets) { if (d >= b.start && d < b.end) { b.value += valSel(item); break; } }
  });
  return { labels: buckets.map(b => b.label), values: buckets.map(b => Math.round(b.value * 100) / 100) };
};

const sumInPeriod = (items: ItemRecord[], iv: TimeInterval, period: Date, dateSel: DateSel, valSel: ValSel): number => {
  const s = startOfInterval(period, iv);
  const e = addInterval(s, iv, 1);
  return items.reduce((t, item) => { const d = dateSel(item); return d && new Date(d) >= s && new Date(d) < e ? t + valSel(item) : t; }, 0);
};

const countInPeriod = (items: ItemRecord[], iv: TimeInterval, period: Date, dateSel: DateSel): number =>
  sumInPeriod(items, iv, period, dateSel, () => 1);

const pctChange = (curr: number, prev: number): number =>
  prev === 0 ? (curr > 0 ? 100 : 0) : Math.round(((curr - prev) / prev) * 1000) / 10;

const fmt$ = (v: number): string => v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v / 1e3).toFixed(1)}K` : `$${v.toFixed(0)}`;
const fmtN = (v: number): string => v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(1)}K` : `${v}`;

const PALETTE = [
  '#6366f1', '#10b981', '#f59e0b', '#ef4444', '#06b6d4',
  '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#84cc16',
  '#3b82f6', '#d946ef', '#0ea5e9', '#a855f7', '#22d3ee',
  '#e11d48', '#059669', '#7c3aed', '#0284c7', '#ca8a04',
  '#f472b6', '#fb923c', '#a3e635', '#34d399', '#2dd4bf',
  '#38bdf8', '#818cf8', '#c084fc', '#f0abfc', '#fda4af',
];

const createdDate: DateSel = i => i.createdAt ? new Date(i.createdAt) : null;
const purchaseOrCreated: DateSel = i => i.purchaseDate ? new Date(i.purchaseDate) : i.createdAt ? new Date(i.createdAt) : null;
const priceVal: ValSel = i => i.purchasePrice || 0;

/* ──────────────────────────── Component ──────────────────────────── */

const Dashboard = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [allItems, setAllItems] = useState<ItemRecord[]>([]);
  const [categoryCount, setCategoryCount] = useState(0);

  const [globalInterval, setGlobalInterval] = useState<TimeInterval>('month');
  const [globalPeriod] = useState<Date>(new Date());

  const [charts, setCharts] = useState<DashboardChartConfig[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showCustomizer, setShowCustomizer] = useState(false);
  const [draggedChart, setDraggedChart] = useState<string | null>(null);

  const [showChartBuilder, setShowChartBuilder] = useState(false);
  const [builderStep, setBuilderStep] = useState<'type' | 'data' | 'preview'>('type');
  const [newChartType, setNewChartType] = useState<'bar' | 'pie' | 'line' | 'number'>('bar');
  const [newChartTitle, setNewChartTitle] = useState('');
  const [newChartDescription, setNewChartDescription] = useState('');
  const [newChartDataPoints, setNewChartDataPoints] = useState<{ label: string; value: string; color: string }[]>([{ label: '', value: '', color: '#6366f1' }]);

  const { t } = useI18n();

  /* ────── Data Fetch ────── */

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [itemsRes, catsRes] = await Promise.all([api.get('/items?take=5'), api.get('/categories')]);
      dispatch(setItems({ items: itemsRes.data.items, total: itemsRes.data.total }));
      const allRes = await api.get('/items?take=10000');
      const fetchedItems = (allRes.data.items as ItemRecord[]).filter((item: any) => !item.isLocked);
      setAllItems(fetchedItems);
      setCategoryCount(catsRes.data.length);
    } catch (err) {
      console.error('Dashboard data fetch failed:', err);
      setAllItems([]);
      setCategoryCount(0);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ────── KPI Cards ────── */

  const kpis = useMemo<KPI[]>(() => {
    const now = globalPeriod;
    const prev = addInterval(startOfInterval(now, globalInterval), globalInterval, -1);

    const totalItems = allItems.length;
    const prevItems = countInPeriod(allItems, globalInterval, prev, createdDate);
    const currItems = countInPeriod(allItems, globalInterval, now, createdDate);

    const totalValue = allItems.reduce((s, i) => s + (i.purchasePrice || 0), 0);
    const currValue = sumInPeriod(allItems, globalInterval, now, purchaseOrCreated, priceVal);
    const prevValue = sumInPeriod(allItems, globalInterval, prev, purchaseOrCreated, priceVal);

    const favorites = allItems.filter(i => i.isFavorite).length;
    const avgPrice = totalItems > 0 ? totalValue / totalItems : 0;

    return [
      { id: 'total-items', label: t('dashboard.totalItems'), value: totalItems, formatted: fmtN(totalItems), change: pctChange(currItems, prevItems), changeLabel: t('dashboard.newThisPeriod'), icon: faCubes, color: '#6366f1' },
      { id: 'total-value', label: t('dashboard.portfolioValue'), value: totalValue, formatted: fmt$(totalValue), change: pctChange(currValue, prevValue), changeLabel: t('dashboard.vsLastPeriod'), icon: faWallet, color: '#10b981' },
      { id: 'favorites', label: t('dashboard.favorites'), value: favorites, formatted: fmtN(favorites), change: 0, changeLabel: t('dashboard.marked'), icon: faGem, color: '#f59e0b' },
      { id: 'avg-value', label: t('dashboard.avgItemValue'), value: avgPrice, formatted: fmt$(avgPrice), change: 0, changeLabel: t('dashboard.perItem'), icon: faLayerGroup, color: '#06b6d4' },
    ];
  }, [allItems, globalInterval, globalPeriod, t]);

  /* ────── Chart Builders ────── */

  const buildValueTrend = useCallback((iv: TimeInterval, period: Date) => {
    const s = buildTimeSeries(allItems, iv, period, purchaseOrCreated, priceVal);
    return { ...s, colors: s.labels.map(() => '#10b981') };
  }, [allItems]);

  const buildActivityTrend = useCallback((iv: TimeInterval, period: Date) => {
    const s = buildTimeSeries(allItems, iv, period, createdDate, () => 1);
    return { ...s, colors: s.labels.map(() => '#6366f1') };
  }, [allItems]);

  const buildConditionBreakdown = useCallback(() => {
    const COND_COLORS: Record<string, string> = { MINT: '#10b981', EXCELLENT: '#06b6d4', GOOD: '#3b82f6', FAIR: '#f59e0b', POOR: '#ef4444', UNKNOWN: '#64748b' };
    const counts: Record<string, number> = {};
    allItems.forEach(i => { const c = i.condition || 'UNKNOWN'; counts[c] = (counts[c] || 0) + 1; });
    return { labels: Object.keys(counts), values: Object.values(counts), colors: Object.keys(counts).map(k => COND_COLORS[k] || PALETTE[Object.keys(counts).indexOf(k) % PALETTE.length]) };
  }, [allItems]);

  const buildCategoryBreakdown = useCallback(() => {
    const CAT_COLORS = ['#6366f1', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#8b5cf6', '#ef4444', '#14b8a6'];
    const counts: Record<string, number> = {};
    allItems.forEach(i => { const n = i.category?.name || 'Uncategorized'; counts[n] = (counts[n] || 0) + 1; });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 8);
    return { labels: sorted.map(e => e[0]), values: sorted.map(e => e[1]), colors: sorted.map((_, i) => CAT_COLORS[i % CAT_COLORS.length]) };
  }, [allItems]);

  const buildLocationBreakdown = useCallback(() => {
    const LOC_COLORS = ['#0ea5e9', '#22d3ee', '#06b6d4', '#14b8a6', '#10b981', '#059669', '#84cc16', '#a3e635'];
    const counts: Record<string, number> = {};
    allItems.forEach(i => { const l = i.location || 'Unspecified'; counts[l] = (counts[l] || 0) + 1; });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 8);
    return { labels: sorted.map(e => e[0]), values: sorted.map(e => e[1]), colors: sorted.map((_, i) => LOC_COLORS[i % LOC_COLORS.length]) };
  }, [allItems]);

  const buildPriceDistribution = useCallback(() => {
    const PRICE_COLORS = ['#22d3ee', '#06b6d4', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899'];
    const ranges = [
      { label: '$0–$10', min: 0, max: 10 }, { label: '$10–$25', min: 10, max: 25 },
      { label: '$25–$50', min: 25, max: 50 }, { label: '$50–$100', min: 50, max: 100 },
      { label: '$100–$250', min: 100, max: 250 }, { label: '$250–$500', min: 250, max: 500 },
      { label: '$500–$1K', min: 500, max: 1000 }, { label: '$1K+', min: 1000, max: Infinity },
    ];
    const values = ranges.map(r => allItems.filter(i => (i.purchasePrice || 0) >= r.min && (i.purchasePrice || 0) < r.max).length);
    return { labels: ranges.map(r => r.label), values, colors: PRICE_COLORS };
  }, [allItems]);

  const buildTopItems = useCallback(() => {
    const TOP_COLORS = ['#f59e0b', '#f97316', '#ef4444', '#ec4899', '#d946ef', '#a855f7', '#8b5cf6', '#6366f1'];
    const sorted = [...allItems].sort((a, b) => (b.purchasePrice || 0) - (a.purchasePrice || 0)).slice(0, 8);
    return {
      labels: sorted.map(i => i.name.length > 20 ? i.name.substring(0, 17) + '...' : i.name),
      values: sorted.map(i => i.purchasePrice || 0),
      colors: TOP_COLORS,
    };
  }, [allItems]);

  const buildCumulativeValue = useCallback((iv: TimeInterval, period: Date) => {
    const series = buildTimeSeries(allItems, iv, period, purchaseOrCreated, priceVal);
    let cum = 0;
    const cumulativeValues = series.values.map(v => { cum += v; return Math.round(cum * 100) / 100; });
    return { labels: series.labels, values: cumulativeValues, colors: series.labels.map(() => '#8b5cf6') };
  }, [allItems]);

  const buildFavoritesTrend = useCallback((iv: TimeInterval, period: Date) => {
    const s = buildTimeSeries(allItems.filter(i => i.isFavorite), iv, period, createdDate, () => 1);
    return { ...s, colors: s.labels.map(() => '#f59e0b') };
  }, [allItems]);

  const buildTagsDistribution = useCallback(() => {
    const TAG_COLORS = ['#ec4899', '#f472b6', '#f0abfc', '#d946ef', '#c084fc', '#a855f7', '#818cf8', '#6366f1'];
    const tagCounts: Record<string, number> = {};
    allItems.forEach(i => {
      (i.tags || []).forEach(tag => {
        tagCounts[tag] = (tagCounts[tag] || 0) + 1;
      });
    });
    const sorted = Object.entries(tagCounts).sort((a, b) => b[1] - a[1]).slice(0, 8);
    return {
      labels: sorted.map(e => e[0]),
      values: sorted.map(e => e[1]),
      colors: sorted.map((_, i) => TAG_COLORS[i % TAG_COLORS.length])
    };
  }, [allItems]);

  const buildPurchasePlaceBreakdown = useCallback(() => {
    const PLACE_COLORS = ['#fbbf24', '#fb923c', '#f59e0b', '#f97316', '#ea580c', '#dc2626', '#c026d3', '#9333ea'];
    const counts: Record<string, number> = {};
    allItems.forEach(i => { const p = (i as any).purchasePlace || 'Unknown'; counts[p] = (counts[p] || 0) + 1; });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 8);
    return { labels: sorted.map(e => e[0]), values: sorted.map(e => e[1]), colors: sorted.map((_, i) => PLACE_COLORS[i % PLACE_COLORS.length]) };
  }, [allItems]);

  const buildAveragePriceByCategory = useCallback(() => {
    const counts: Record<string, { total: number; count: number }> = {};
    allItems.forEach(i => {
      const cat = i.category?.name || 'Uncategorized';
      if (!counts[cat]) counts[cat] = { total: 0, count: 0 };
      counts[cat].total += i.purchasePrice || 0;
      counts[cat].count += 1;
    });
    const sorted = Object.entries(counts)
      .map(([name, data]) => ({ name, avg: data.count > 0 ? data.total / data.count : 0 }))
      .sort((a, b) => b.avg - a.avg)
      .slice(0, 8);
    const AVG_COLORS = ['#10b981', '#059669', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6'];
    return {
      labels: sorted.map(e => e.name),
      values: sorted.map(e => Math.round(e.avg)),
      colors: AVG_COLORS
    };
  }, [allItems]);

  /* ────── Chart Computation ────── */

  const computeChart = useCallback((c: DashboardChartConfig, iv: TimeInterval, period: Date): DashboardChartConfig => {
    const p = c.currentPeriod ? new Date(c.currentPeriod) : period;
    const cIv = c.interval || iv;
    switch (c.id) {
      case 'value-trend': return { ...c, interval: cIv, currentPeriod: p, data: buildValueTrend(cIv, p) };
      case 'activity-trend': return { ...c, interval: cIv, currentPeriod: p, data: buildActivityTrend(cIv, p) };
      case 'condition-breakdown': return { ...c, data: buildConditionBreakdown() };
      case 'category-breakdown': return { ...c, data: buildCategoryBreakdown() };
      case 'location-breakdown': return { ...c, data: buildLocationBreakdown() };
      case 'price-distribution': return { ...c, data: buildPriceDistribution() };
      case 'top-items': return { ...c, data: buildTopItems() };
      case 'cumulative-value': return { ...c, interval: cIv, currentPeriod: p, data: buildCumulativeValue(cIv, p) };
      case 'favorites-trend': return { ...c, interval: cIv, currentPeriod: p, data: buildFavoritesTrend(cIv, p) };
      case 'tags-distribution': return { ...c, data: buildTagsDistribution() };
      case 'purchase-place': return { ...c, data: buildPurchasePlaceBreakdown() };
      case 'avg-price-category': return { ...c, data: buildAveragePriceByCategory() };
      default: return c;
    }
  }, [buildValueTrend, buildActivityTrend, buildConditionBreakdown, buildCategoryBreakdown, buildLocationBreakdown, buildPriceDistribution, buildTopItems, buildCumulativeValue, buildFavoritesTrend, buildTagsDistribution, buildPurchasePlaceBreakdown, buildAveragePriceByCategory]);

  // Initialize charts when data loads
  useEffect(() => {
    if (loading) return;
    const now = globalPeriod;
    const iv = globalInterval;

    const saved = localStorage.getItem('dashboard_v2');
    if (saved) {
      try {
        const parsed: DashboardChartConfig[] = JSON.parse(saved);
        setCharts(parsed.map(c => computeChart(c, iv, now)));
        return;
      } catch { /* ignore */ }
    }

    const defaults: DashboardChartConfig[] = [
      { id: 'value-trend', title: '', titleKey: 'dashboard.valueTrend', type: 'line', data: {}, interval: iv, currentPeriod: now, visible: true, size: 'wide', description: '', descKey: 'dashboard.valueTrendDesc' },
      { id: 'activity-trend', title: '', titleKey: 'dashboard.itemsAdded', type: 'bar', data: {}, interval: iv, currentPeriod: now, visible: true, description: '', descKey: 'dashboard.itemsAddedDesc' },
      { id: 'condition-breakdown', title: '', titleKey: 'dashboard.conditionBreakdown', type: 'pie', data: {}, visible: true, description: '', descKey: 'dashboard.conditionDesc' },
      { id: 'category-breakdown', title: '', titleKey: 'dashboard.categoryDistribution', type: 'pie', data: {}, visible: true, description: '', descKey: 'dashboard.categoryDesc' },
      { id: 'price-distribution', title: '', titleKey: 'dashboard.priceDistribution', type: 'bar', data: {}, visible: true, description: '', descKey: 'dashboard.priceDistDesc' },
      { id: 'top-items', title: '', titleKey: 'dashboard.mostValuable', type: 'bar', data: {}, visible: true, description: '', descKey: 'dashboard.mostValuableDesc' },
      { id: 'location-breakdown', title: '', titleKey: 'dashboard.byLocation', type: 'bar', data: {}, visible: true, description: '', descKey: 'dashboard.byLocationDesc' },
      { id: 'cumulative-value', title: '', titleKey: 'dashboard.cumulativeValue', type: 'line', data: {}, interval: iv, currentPeriod: now, visible: true, size: 'wide', description: '', descKey: 'dashboard.cumulativeDesc' },
      { id: 'favorites-trend', title: '', titleKey: 'dashboard.favoritesOverTime', type: 'line', data: {}, interval: iv, currentPeriod: now, visible: true, description: '', descKey: 'dashboard.favTrendDesc' },
      { id: 'tags-distribution', title: '', titleKey: 'dashboard.topTags', type: 'pie', data: {}, visible: true, description: '', descKey: 'dashboard.topTagsDesc' },
      { id: 'purchase-place', title: '', titleKey: 'dashboard.purchaseSources', type: 'bar', data: {}, visible: true, description: '', descKey: 'dashboard.purchaseDesc' },
      { id: 'avg-price-category', title: '', titleKey: 'dashboard.avgByCategory', type: 'bar', data: {}, visible: true, description: '', descKey: 'dashboard.avgCatDesc' },
    ];
    setCharts(defaults.map(c => computeChart(c, iv, now)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, allItems.length]);

  /* ────── Handlers ────── */

  const handleGlobalIntervalChange = (iv: TimeInterval) => {
    setGlobalInterval(iv);
    setCharts(prev => prev.map(c => c.interval ? computeChart({ ...c, interval: iv }, iv, globalPeriod) : c));
  };

  const handleChartIntervalChange = (chartId: string, iv: TimeInterval) => {
    setCharts(prev => prev.map(c => c.id !== chartId ? c : computeChart({ ...c, interval: iv }, iv, c.currentPeriod || globalPeriod)));
  };

  const handleChartPeriodChange = (chartId: string, dir: 'prev' | 'next') => {
    setCharts(prev => prev.map(c => {
      if (c.id !== chartId) return c;
      const iv = c.interval || globalInterval;
      const next = addInterval(c.currentPeriod || globalPeriod, iv, dir === 'prev' ? -1 : 1);
      return computeChart({ ...c, currentPeriod: next }, iv, next);
    }));
  };

  const handleRemoveChart = (id: string) => setCharts(prev => prev.filter(c => c.id !== id));
  const handleReorderChart = (fromId: string, toId: string) => {
    const fi = charts.findIndex(c => c.id === fromId);
    const ti = charts.findIndex(c => c.id === toId);
    if (fi === -1 || ti === -1) return;
    const n = [...charts]; [n[fi], n[ti]] = [n[ti], n[fi]]; setCharts(n);
  };
  const toggleVisibility = (id: string) => setCharts(prev => prev.map(c => c.id === id ? { ...c, visible: !c.visible } : c));

  const saveDashboard = () => { localStorage.setItem('dashboard_v2', JSON.stringify(charts)); setIsEditMode(false); setShowCustomizer(false); };
  const handleRefresh = () => { localStorage.removeItem('dashboard_v2'); fetchData(); };

  /* ────── Convert to ChartData ────── */

  const toChartData = (c: DashboardChartConfig): ChartData => {
    const dataPoints: ChartDataPoint[] = (() => {
      if (c.data.labels && c.data.values) {
        return c.data.labels.map((label, i) => ({ label, value: c.data.values![i] || 0, color: c.data.colors?.[i] }));
      }
      if (c.data.value !== undefined && c.data.label) {
        return [{ label: c.data.label, value: typeof c.data.value === 'number' ? c.data.value : parseFloat(String(c.data.value).replace(/[^0-9.-]/g, '')) || 0 }];
      }
      return [];
    })();

    // Translate title/description dynamically using keys
    const i18nKeys = CHART_I18N[c.id];
    const chartTitle = c.titleKey ? t(c.titleKey as any) : (i18nKeys ? t(i18nKeys.titleKey as any) : c.title);
    const chartDesc = c.descKey ? t(c.descKey as any) : (i18nKeys ? t(i18nKeys.descKey as any) : c.description);

    return {
      id: c.id, title: chartTitle, type: c.type, data: dataPoints, description: chartDesc,
      interval: c.interval, currentPeriod: c.currentPeriod,
      trend: c.data.change !== undefined ? { value: Math.abs(c.data.change), direction: c.data.change > 0 ? 'up' : c.data.change < 0 ? 'down' : 'stable' } : undefined,
    };
  };

  /* ────── Chart Builder ────── */

  const handleAddChart = () => { setShowChartBuilder(true); setBuilderStep('type'); setNewChartType('bar'); setNewChartTitle(''); setNewChartDescription(''); setNewChartDataPoints([{ label: '', value: '', color: '#6366f1' }]); };
  const handleAddDataPoint = () => { setNewChartDataPoints([...newChartDataPoints, { label: '', value: '', color: PALETTE[newChartDataPoints.length % PALETTE.length] }]); };
  const handleRemoveDataPoint = (i: number) => { if (newChartDataPoints.length > 1) setNewChartDataPoints(newChartDataPoints.filter((_, idx) => idx !== i)); };
  const handleUpdateDataPoint = (i: number, field: 'label' | 'value' | 'color', val: string) => { const u = [...newChartDataPoints]; u[i] = { ...u[i], [field]: val }; setNewChartDataPoints(u); };

  const handleCreateChart = () => {
    const valid = newChartDataPoints.filter(p => p.label.trim() && p.value.trim());
    if (!newChartTitle.trim()) return;
    if (newChartType !== 'number' && valid.length === 0) return;
    const chart: DashboardChartConfig = {
      id: `custom-${Date.now()}`, title: newChartTitle.trim(), type: newChartType,
      data: newChartType === 'number'
        ? { value: valid[0] ? parseFloat(valid[0].value) || 0 : 0, label: valid[0]?.label || newChartTitle, change: 0 }
        : { labels: valid.map(p => p.label), values: valid.map(p => parseFloat(p.value) || 0), colors: valid.map(p => p.color) },
      description: newChartDescription.trim() || undefined, visible: true,
    };
    setCharts(prev => [...prev, chart]);
    setShowChartBuilder(false);
  };

  const chartTypeOptions = [
    { type: 'bar' as const, icon: faChartBar, label: t('dashboard.barChart'), desc: t('dashboard.barChartDesc') },
    { type: 'pie' as const, icon: faChartPie, label: t('dashboard.pieChart'), desc: t('dashboard.pieChartDesc') },
    { type: 'line' as const, icon: faChartLine, label: t('dashboard.lineChart'), desc: t('dashboard.lineChartDesc') },
    { type: 'number' as const, icon: faHashtag, label: t('dashboard.metricCard'), desc: t('dashboard.metricCardDesc') },
  ];

  /* ────── Period Label ────── */
  const periodLabel = useMemo(() => {
    const d = globalPeriod;
    switch (globalInterval) {
      case 'day': return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      case 'week': { const ws = new Date(d); ws.setDate(d.getDate() - d.getDay()); const we = new Date(ws); we.setDate(ws.getDate() + 6); return `${ws.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${we.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`; }
      case 'month': return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      case 'quarter': return `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`;
      case 'year': return `${d.getFullYear()}`;
    }
  }, [globalPeriod, globalInterval]);

  /* ────── Render ────── */

  if (loading) {
    return <div className="dashboard-loading"><div className="loading-spinner" /><span>{t('dashboard.loading')}</span></div>;
  }

  const visibleCharts = charts.filter(c => c.visible !== false);

  return (
    <div className={`dashboard ${showCustomizer ? 'customizer-open' : ''}`}>
      <header className="dash-header">
        <div className="dash-header-left">
          <h1>{t('dashboard.analytics')}</h1>
          <span className="dash-subtitle">{allItems.length} {t('dashboard.itemsTracked')} · {categoryCount} {t('dashboard.categoriesLabel')}</span>
        </div>
        <div className="dash-header-actions">
          <button className="dash-btn" onClick={handleRefresh} title="Refresh data"><FontAwesomeIcon icon={faSync} /></button>
          <button className={`dash-btn ${isEditMode ? 'active' : ''}`} onClick={() => setIsEditMode(!isEditMode)}><FontAwesomeIcon icon={faEdit} /> {t('dashboard.customize')}</button>
          <button className={`dash-btn ${showCustomizer ? 'active' : ''}`} onClick={() => setShowCustomizer(s => !s)}><FontAwesomeIcon icon={faSliders} /> {t('dashboard.widgets')}</button>
          {(isEditMode || showCustomizer) && <button className="dash-btn primary" onClick={saveDashboard}><FontAwesomeIcon icon={faSave} /> {t('dashboard.save')}</button>}
        </div>
      </header>

      <div className="dash-controls">
        <div className="interval-pills">
          {(['day', 'week', 'month', 'quarter', 'year'] as TimeInterval[]).map(iv => (
            <button key={iv} className={`pill ${globalInterval === iv ? 'active' : ''}`} onClick={() => handleGlobalIntervalChange(iv)}>
              {t(`dashboard.${iv}` as any)}
            </button>
          ))}
        </div>
        <div className="period-nav">
          <span className="period-label-text"><FontAwesomeIcon icon={faCalendarAlt} /> {periodLabel}</span>
        </div>
      </div>

      <div className="kpi-row">
        {kpis.map(kpi => (
          <div key={kpi.id} className="kpi-card" style={{ '--kpi-accent': kpi.color } as React.CSSProperties}>
            <div className="kpi-top-row">
              <span className="kpi-label">{kpi.label}</span>
              <div className="kpi-icon-wrap"><FontAwesomeIcon icon={kpi.icon} /></div>
            </div>
            <span className="kpi-value">{kpi.formatted}</span>
            <div className="kpi-bottom-row">
              <span className={`kpi-change ${kpi.change > 0 ? 'up' : kpi.change < 0 ? 'down' : 'neutral'}`}>
                <FontAwesomeIcon icon={kpi.change > 0 ? faArrowUp : kpi.change < 0 ? faArrowDown : faMinus} />
                {Math.abs(kpi.change)}%
              </span>
              <span className="kpi-change-label">{kpi.changeLabel}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="charts-grid">
        {visibleCharts.map(c => (
          <div key={c.id} className={`chart-cell ${c.size === 'wide' ? 'wide' : ''} ${isEditMode ? 'draggable' : ''}`}
            draggable={isEditMode} onDragStart={() => setDraggedChart(c.id)} onDragOver={e => e.preventDefault()} onDrop={() => draggedChart && handleReorderChart(draggedChart, c.id)}>
            <DashboardChart chart={toChartData(c)} onDelete={isEditMode ? () => handleRemoveChart(c.id) : undefined} onIntervalChange={handleChartIntervalChange} onPeriodChange={handleChartPeriodChange} />
          </div>
        ))}
        {isEditMode && <button className="add-chart-btn" onClick={handleAddChart}><FontAwesomeIcon icon={faPlus} /><span>{t('dashboard.addWidget')}</span></button>}
      </div>

      {showCustomizer && (
        <>
          <div className="dashboard-overlay" onClick={() => setShowCustomizer(false)} />
          <aside className="dashboard-customizer">
            <div className="customizer-header"><h3>{t('dashboard.dashboardWidgets')}</h3><span>{t('dashboard.widgetHint')}</span><button className="panel-close" onClick={() => setShowCustomizer(false)} aria-label="Close">×</button></div>
            <div className="customizer-list">
              {charts.map(c => (
                <div key={c.id} className="customizer-item" draggable onDragStart={() => setDraggedChart(c.id)} onDragOver={e => e.preventDefault()} onDrop={() => draggedChart && handleReorderChart(draggedChart, c.id)}>
                  <span className="drag-icon"><FontAwesomeIcon icon={faGripLines} /></span>
                  <div className="customizer-item-title">{c.titleKey ? t(c.titleKey as any) : (CHART_I18N[c.id] ? t(CHART_I18N[c.id].titleKey as any) : c.title)}</div>
                  <div className={`toggle-switch${c.visible !== false ? ' checked' : ''}`} role="checkbox" aria-checked={c.visible !== false} tabIndex={0} onClick={() => toggleVisibility(c.id)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleVisibility(c.id); } }}><span className="toggle-slider" /></div>
                </div>
              ))}
            </div>
          </aside>
        </>
      )}

      {showChartBuilder && (
        <>
          <div className="dashboard-overlay" onClick={() => setShowChartBuilder(false)} />
          <div className="chart-builder-modal">
            <div className="builder-header">
              <h3>{builderStep === 'type' ? t('dashboard.chooseChartType') : builderStep === 'data' ? t('dashboard.configureData') : t('dashboard.previewCreate')}</h3>
              <div className="builder-steps">
                <span className={`step ${builderStep === 'type' ? 'active' : 'done'}`}>{t('dashboard.stepType')}</span>
                <span className={`step ${builderStep === 'data' ? 'active' : builderStep === 'preview' ? 'done' : ''}`}>{t('dashboard.stepData')}</span>
                <span className={`step ${builderStep === 'preview' ? 'active' : ''}`}>{t('dashboard.stepPreview')}</span>
              </div>
              <button className="panel-close" onClick={() => setShowChartBuilder(false)}><FontAwesomeIcon icon={faTimes} /></button>
            </div>
            <div className="builder-body">
              {builderStep === 'type' && (
                <div className="builder-type-grid">
                  {chartTypeOptions.map(opt => (
                    <div key={opt.type} className={`builder-type-card ${newChartType === opt.type ? 'selected' : ''}`} onClick={() => setNewChartType(opt.type)}>
                      <FontAwesomeIcon icon={opt.icon} className="type-icon" /><h4>{opt.label}</h4><p>{opt.desc}</p>
                    </div>
                  ))}
                </div>
              )}
              {builderStep === 'data' && (
                <div className="builder-data-form">
                  <div className="form-group"><label>{t('dashboard.chartTitle')}</label><input type="text" placeholder="e.g., Monthly Sales" value={newChartTitle} onChange={e => setNewChartTitle(e.target.value)} autoFocus /></div>
                  <div className="form-group"><label>{t('dashboard.description')}</label><input type="text" placeholder="Brief description" value={newChartDescription} onChange={e => setNewChartDescription(e.target.value)} /></div>
                  <div className="form-group">
                    <label>{newChartType === 'number' ? t('dashboard.value') : t('dashboard.dataPoints')}</label>
                    <div className="data-points-list">
                      {newChartDataPoints.map((pt, idx) => (
                        <div key={idx} className="data-point-row">
                          <input type="text" placeholder={`Label ${idx + 1}`} value={pt.label} onChange={e => handleUpdateDataPoint(idx, 'label', e.target.value)} />
                          <input type="number" placeholder="Value" value={pt.value} onChange={e => handleUpdateDataPoint(idx, 'value', e.target.value)} />
                          <div className="color-picker-mini"><input type="color" value={pt.color} onChange={e => handleUpdateDataPoint(idx, 'color', e.target.value)} /><FontAwesomeIcon icon={faPalette} /></div>
                          {newChartDataPoints.length > 1 && <button className="remove-point-btn" onClick={() => handleRemoveDataPoint(idx)}><FontAwesomeIcon icon={faTrash} /></button>}
                        </div>
                      ))}
                    </div>
                    {newChartType !== 'number' && <button className="add-point-btn" onClick={handleAddDataPoint}><FontAwesomeIcon icon={faPlus} /> {t('dashboard.addDataPoint')}</button>}
                  </div>
                </div>
              )}
              {builderStep === 'preview' && (
                <div className="builder-preview">
                  <div className="preview-info">
                    <div className="preview-detail"><strong>Type:</strong> {chartTypeOptions.find(o => o.type === newChartType)?.label}</div>
                    <div className="preview-detail"><strong>Title:</strong> {newChartTitle || '(untitled)'}</div>
                    <div className="preview-detail"><strong>Points:</strong> {newChartDataPoints.filter(p => p.label && p.value).length}</div>
                  </div>
                  <div className="preview-chart-wrapper">
                    <DashboardChart chart={{ id: 'preview', title: newChartTitle || 'Preview', type: newChartType, data: newChartType === 'number' ? [{ label: newChartDataPoints[0]?.label || 'Value', value: parseFloat(newChartDataPoints[0]?.value) || 0 }] : newChartDataPoints.filter(p => p.label && p.value).map(p => ({ label: p.label, value: parseFloat(p.value) || 0, color: p.color })) }} onIntervalChange={() => {}} onPeriodChange={() => {}} />
                  </div>
                </div>
              )}
            </div>
            <div className="builder-footer">
              {builderStep !== 'type' && <button className="btn-builder-back" onClick={() => setBuilderStep(builderStep === 'preview' ? 'data' : 'type')}>{t('dashboard.back')}</button>}
              <div style={{ flex: 1 }} />
              {builderStep === 'type' && <button className="btn-builder-next" onClick={() => setBuilderStep('data')}>{t('dashboard.nextConfigure')}</button>}
              {builderStep === 'data' && <button className="btn-builder-next" onClick={() => setBuilderStep('preview')}>{t('dashboard.nextPreview')}</button>}
              {builderStep === 'preview' && <button className="btn-builder-create" onClick={handleCreateChart}><FontAwesomeIcon icon={faPlus} /> {t('dashboard.createChart')}</button>}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
